/** @jsx jsx */
import { SingleSelect, SingleSelectProps } from "@eworkit/iris";
import { POPULAR_COUNTRY_CODES } from "consts";
import { FC, useCallback, useMemo } from "react";
import { jsx } from "theme-ui";

import { withFormikField } from "@/components/Formik/withFormikField";
import { fetchCodelist, ICodelist } from "@/data/codelists";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFetcher } from "@/hooks/http";

interface GroupedCountriesSingleSelectProps {
  value?: string | null;
  onChange?: (value?: string) => void;
  size?: SingleSelectProps["size"];
  disabled?: boolean;
}

export const GroupedCountryCodesSingleSelect: FC<GroupedCountriesSingleSelectProps> = ({
  value,
  onChange,
  ...selectProps
}) => {
  const tCodelist = useCodelistTranslation();

  const fetcher = useFetcher<{}, ICodelist[]>(fetchCodelist, { codelistName: "country-codes" }, []);

  const options = useMemo(() => {
    if (fetcher.payload) {
      const sortedCountries = fetcher.payload.sort((a, b) =>
        tCodelist("country-codes", a.code) > tCodelist("country-codes", b.code) ? 1 : -1,
      );

      const popularCountries = sortedCountries
        .filter(c => POPULAR_COUNTRY_CODES.includes(c.code))
        .map(c => ({
          ...c,
          group: {
            key: "popular",
          },
        }));
      const restCountries = sortedCountries
        .filter(c => !POPULAR_COUNTRY_CODES.includes(c.code))
        .map(c => ({
          ...c,
          group: {
            key: "rest",
          },
        }));

      return [...popularCountries, ...restCountries];
    }

    return undefined;
  }, [fetcher.payload]);

  const handleChange = useCallback(
    (newValue?: ICodelist) => {
      onChange?.(newValue?.code);
    },
    [onChange],
  );

  return (
    <SingleSelect
      options={options}
      value={options?.find(o => o.code === value)}
      onChange={handleChange}
      labelSelector={o => tCodelist("country-codes", o.code)}
      identifierSelector={o => o.code}
      working={fetcher.working}
      searchable
      {...selectProps}
    />
  );
};

export const FormikGroupedCountryCodesSingleSelect = withFormikField(GroupedCountryCodesSingleSelect);
